/**
 * ContextAttachPopup - Enhanced file and context attachment popup
 * 
 * Features:
 * - Image URL attachment
 * - File upload (drag & drop)
 * - Text context attachment
 * - URL content fetching
 * - Better UX with tabs
 */

import React, { memo, useCallback, useState, useRef } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
    Button,
    PopupWrapper,
    TextLabel,
    BiImageAlt,
    BiLink,
    BiFile,
    BiText,
    BiTrash,
    BsPlus,
    BsUpload,
    BsGlobe,
    IoMdClose,
    toast,
} from '@acme/ui';
import { modelPopupAtom } from '../store/instruction';
import { composeContextImgsAtom, composeContextFilesAtom } from '../store/util';

// =============================================================================
// TYPES
// =============================================================================

type AttachmentTab = 'images' | 'text' | 'url';

interface ContextFile {
    id: string;
    name: string;
    content: string;
    type: 'text' | 'code' | 'markdown';
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ContextAttachPopup = memo(() => {
    const [popup, setPopup] = useAtom(modelPopupAtom);
    const [activeTab, setActiveTab] = useState<AttachmentTab>('images');

    const isOpen = popup.open && popup.type === 'attach-file';

    const handleClose = useCallback(() => {
        setPopup({ open: false, type: '' });
    }, [setPopup]);

    if (!isOpen) return null;

    return (
        <PopupWrapper
            show={isOpen}
            onClose={handleClose}
            className="!bg-black/60"
            containerClassName="z-[100002]"
        >
            <div className="w-full max-w-lg bg-white dark:bg-neutral-900 rounded-xl shadow-2xl overflow-hidden mx-4">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-neutral-700">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        📎 Attach Context
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Add images, text, or web content to your message
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-neutral-700">
                    <TabButton
                        active={activeTab === 'images'}
                        onClick={() => setActiveTab('images')}
                        icon={<BiImageAlt />}
                        label="Images"
                    />
                    <TabButton
                        active={activeTab === 'text'}
                        onClick={() => setActiveTab('text')}
                        icon={<BiText />}
                        label="Text"
                    />
                    <TabButton
                        active={activeTab === 'url'}
                        onClick={() => setActiveTab('url')}
                        icon={<BsGlobe />}
                        label="URL"
                    />
                </div>

                {/* Content */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {activeTab === 'images' && <ImagesTab />}
                    {activeTab === 'text' && <TextTab />}
                    {activeTab === 'url' && <URLTab />}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-neutral-700 flex justify-end">
                    <Button
                        onClick={handleClose}
                        className="!bg-blue-600 hover:!bg-blue-700 !text-white px-6"
                    >
                        Done
                    </Button>
                </div>
            </div>
        </PopupWrapper>
    );
});

ContextAttachPopup.displayName = 'ContextAttachPopup';

// =============================================================================
// TAB BUTTON
// =============================================================================

interface TabButtonProps {
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
}

const TabButton = memo(({ active, onClick, icon, label }: TabButtonProps) => (
    <button
        onClick={onClick}
        className={`
            flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors
            ${active
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }
        `}
    >
        {icon}
        <span>{label}</span>
    </button>
));

TabButton.displayName = 'TabButton';

// =============================================================================
// IMAGES TAB
// =============================================================================

const ImagesTab = memo(() => {
    const [images, setImages] = useAtom(composeContextImgsAtom);
    const [urlInput, setUrlInput] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const addImageUrl = useCallback(() => {
        if (!urlInput.trim()) return;

        if (!urlInput.startsWith('http://') && !urlInput.startsWith('https://')) {
            toast.error('Please enter a valid URL');
            return;
        }

        setImages(prev => [...prev, { url: urlInput.trim(), type: 'image' }]);
        setUrlInput('');
        toast.success('Image added');
    }, [urlInput, setImages]);

    const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        Array.from(files).forEach(file => {
            if (!file.type.startsWith('image/')) {
                toast.error(`${file.name} is not an image`);
                return;
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                const dataUrl = event.target?.result as string;
                setImages(prev => [...prev, { url: dataUrl, type: 'image' }]);
            };
            reader.readAsDataURL(file);
        });

        toast.success(`${files.length} image(s) added`);
    }, [setImages]);

    const removeImage = useCallback((index: number) => {
        setImages(prev => prev.filter((_, i) => i !== index));
    }, [setImages]);

    return (
        <div className="space-y-4">
            {/* URL input */}
            <div className="flex gap-2">
                <input
                    type="text"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addImageUrl()}
                    placeholder="Paste image URL..."
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <Button onClick={addImageUrl} className="!bg-blue-500 !text-white">
                    <BsPlus className="text-lg" />
                </Button>
            </div>

            {/* Upload button */}
            <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 dark:border-neutral-600 rounded-lg text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
            >
                <BsUpload className="text-lg" />
                <span className="text-sm font-medium">Upload from device</span>
            </button>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileUpload}
            />

            {/* Image list */}
            {images.length > 0 && (
                <div className="space-y-2">
                    <TextLabel size="sm">{images.length} image(s) attached</TextLabel>
                    <div className="grid grid-cols-3 gap-2">
                        {images.map((img, index) => (
                            <div key={index} className="relative group aspect-square">
                                <img
                                    src={img.url}
                                    alt={`Attachment ${index + 1}`}
                                    className="w-full h-full object-cover rounded-lg border border-gray-200 dark:border-neutral-700"
                                />
                                <button
                                    onClick={() => removeImage(index)}
                                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <IoMdClose className="text-xs" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {images.length === 0 && (
                <p className="text-center text-sm text-gray-400 dark:text-gray-500 py-4">
                    No images attached yet
                </p>
            )}
        </div>
    );
});

ImagesTab.displayName = 'ImagesTab';

// =============================================================================
// TEXT TAB
// =============================================================================

const TextTab = memo(() => {
    const [files, setFiles] = useAtom(composeContextFilesAtom);
    const [textInput, setTextInput] = useState('');
    const [fileName, setFileName] = useState('');

    const addTextContext = useCallback(() => {
        if (!textInput.trim()) {
            toast.error('Please enter some text');
            return;
        }

        const newFile: ContextFile = {
            id: `text-${Date.now()}`,
            name: fileName.trim() || `Context ${files.length + 1}`,
            content: textInput.trim(),
            type: 'text',
        };

        setFiles(prev => [...prev, newFile]);
        setTextInput('');
        setFileName('');
        toast.success('Text context added');
    }, [textInput, fileName, files.length, setFiles]);

    const removeFile = useCallback((id: string) => {
        setFiles(prev => prev.filter(f => f.id !== id));
    }, [setFiles]);

    return (
        <div className="space-y-4">
            {/* Name input */}
            <div>
                <TextLabel size="sm" className="mb-1">Name (optional)</TextLabel>
                <input
                    type="text"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder="e.g., Project requirements"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white"
                />
            </div>

            {/* Text input */}
            <div>
                <TextLabel size="sm" className="mb-1">Content</TextLabel>
                <textarea
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Paste code, documentation, or any text context..."
                    rows={6}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white font-mono resize-none"
                />
            </div>

            <Button onClick={addTextContext} className="w-full !bg-blue-500 !text-white">
                <BsPlus className="text-lg" />
                Add Text Context
            </Button>

            {/* File list */}
            {files.length > 0 && (
                <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-neutral-700">
                    <TextLabel size="sm">{files.length} text context(s)</TextLabel>
                    {files.map((file) => (
                        <div
                            key={file.id}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-neutral-800 rounded-lg"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <BiFile className="text-gray-400 flex-shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {file.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {file.content.length} characters
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => removeFile(file.id)}
                                className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                            >
                                <BiTrash />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

TextTab.displayName = 'TextTab';

// =============================================================================
// URL TAB
// =============================================================================

const URLTab = memo(() => {
    const [files, setFiles] = useAtom(composeContextFilesAtom);
    const [urlInput, setUrlInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const fetchUrl = useCallback(async () => {
        if (!urlInput.trim()) {
            toast.error('Please enter a URL');
            return;
        }

        if (!urlInput.startsWith('http://') && !urlInput.startsWith('https://')) {
            toast.error('Please enter a valid URL');
            return;
        }

        setIsLoading(true);
        try {
            // Use a simple fetch to get the content
            const response = await fetch(urlInput);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const text = await response.text();

            // Extract text content (strip HTML)
            const content = text
                .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
                .slice(0, 10000); // Limit to 10k chars

            const newFile: ContextFile = {
                id: `url-${Date.now()}`,
                name: new URL(urlInput).hostname,
                content,
                type: 'text',
            };

            setFiles(prev => [...prev, newFile]);
            setUrlInput('');
            toast.success('URL content fetched');
        } catch (error) {
            toast.error('Failed to fetch URL content');
        } finally {
            setIsLoading(false);
        }
    }, [urlInput, setFiles]);

    const removeFile = useCallback((id: string) => {
        setFiles(prev => prev.filter(f => f.id !== id));
    }, [setFiles]);

    return (
        <div className="space-y-4">
            {/* Info */}
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                    🌐 Fetch content from a webpage to use as context. The page content will be extracted as text.
                </p>
            </div>

            {/* URL input */}
            <div className="flex gap-2">
                <input
                    type="text"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchUrl()}
                    placeholder="https://example.com/docs"
                    disabled={isLoading}
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white disabled:opacity-50"
                />
                <Button
                    onClick={fetchUrl}
                    className="!bg-blue-500 !text-white"
                    disabled={isLoading}
                >
                    {isLoading ? 'Fetching...' : 'Fetch'}
                </Button>
            </div>

            {/* Fetched URLs list */}
            {files.filter(f => f.id.startsWith('url-')).length > 0 && (
                <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-neutral-700">
                    <TextLabel size="sm">Fetched pages</TextLabel>
                    {files.filter(f => f.id.startsWith('url-')).map((file) => (
                        <div
                            key={file.id}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-neutral-800 rounded-lg"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <BsGlobe className="text-gray-400 flex-shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {file.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {file.content.length} characters extracted
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => removeFile(file.id)}
                                className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                            >
                                <BiTrash />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

URLTab.displayName = 'URLTab';

// Keep backward compatibility
export const FileAttachPopup = ContextAttachPopup;

export default ContextAttachPopup;
